import React from "react";
import {
  useWSModalCustom,
  WSGrid,
  WSIconList,
  WSInfoBox,
  WSList,
  WSModal,
  WSText
} from "@wingspanhq/fe-component-library";
import { TAX_FILING_UPGRADES_COPY } from "./AlertTaxFilingUndergoingUpgrades";
import { WSIconProps } from "@wingspanhq/fe-component-library/dist/lib/components/core/WSIcon/WSIcon.component";
import { openIntercom } from "../../../../shared/utils/intercom";
import { openInNewTab } from "../../../../shared/utils/openInNewTab";
import { WS_LINKS } from "../../../../types/wsLinks";

const availableIcon: WSIconProps = {
  name: "check-circle",
  size: "S",
  color: "green500"
};

const unavailableIcon: WSIconProps = {
  name: "pause",
  size: "S",
  color: "gray600"
};

const ModalUndergoingUpgrades: React.FC<{
  onClose(result: boolean): void;
}> = ({ onClose }) => {
  return (
    <WSModal
      title="Tax filing undergoing upgrades"
      size="M"
      actions={{
        alignment: "fill",
        buttons: [
          { label: "Done", kind: "Secondary", onClick: () => onClose(false) }
        ]
      }}
      onClose={onClose}
    >
      <WSList gap="L">
        <WSText.ParagraphSm color="gray500">
          {TAX_FILING_UPGRADES_COPY}
        </WSText.ParagraphSm>

        <WSInfoBox title="During the upgrade period (July 31 - September 30):">
          <WSGrid>
            <WSGrid.Item span={{ s: "12", m: "6" }}>
              <WSText.ParagraphSm color="gray600">Available</WSText.ParagraphSm>
              <WSIconList
                textProps={{
                  kind: "ParagraphSm"
                }}
                iconProps={{
                  ...availableIcon
                }}
                inlineIcons={false}
                items={[
                  <>
                    <b>Verify new contractors:</b> TIN verification during the
                    standard contractor onboarding process will continue to
                    operate normally.
                  </>,
                  <>
                    <b>Generate 1099 forms:</b> You can create and save 1099
                    forms, but you will not be able to submit them to the IRS
                    until the upgrade is complete.
                  </>,
                  <>
                    <b>Access all tax data:</b> Your contractor information,
                    W-9s, and historical 1099 data remain fully accessible for
                    viewing and export.
                  </>,
                  <>
                    <b>Continue regular operations:</b> Payments, invoicing, and
                    all other Wingspan features will continue to function
                    normally.
                  </>
                ]}
              />
            </WSGrid.Item>
            <WSGrid.Item span={{ s: "12", m: "6" }}>
              <WSText.ParagraphSm color="gray600">
                Unavailable
              </WSText.ParagraphSm>
              <WSIconList
                textProps={{
                  kind: "ParagraphSm"
                }}
                inlineIcons={false}
                iconProps={{
                  ...unavailableIcon
                }}
                items={[
                  <>
                    <b>Verifying tax form data:</b> TIN verification for
                    manually-entered W-9 information and on 1099 forms is
                    temporarily paused. Verification during initial contractor
                    onboarding remains available.
                  </>,
                  <>
                    <b>Submitting new 1099 forms:</b> E-filing new 1099 forms
                    with the IRS is temporarily paused.
                  </>,
                  <>
                    <b>Submitting corrections:</b> Filing corrections for
                    previously submitted 1099s is temporarily paused.
                  </>,
                  <>
                    <b>Voiding forms:</b> Voiding previously filed 1099 forms is
                    temporarily paused.
                  </>,
                  <>
                    <b>Physical mail services:</b> Sending 1099 forms via
                    physical mail is unavailable.
                  </>
                ]}
              />
            </WSGrid.Item>
          </WSGrid>
        </WSInfoBox>

        <WSText.ParagraphSm color="gray500">
          We expect all standard functionality and upgrades to be available by
          October 1st, 2025. Our team is committed to making this transition as
          smooth as possible. For any questions, please reach out to:
        </WSText.ParagraphSm>

        <WSText.ParagraphSm color="gray500" as="ul" pl="L">
          <li>Your dedicated account manager</li>
          <li>
            Our <a onClick={() => openIntercom()}>support team</a>
          </li>
          <li>
            Our{" "}
            <a onClick={() => openInNewTab(WS_LINKS.contractorSupportCenter)}>
              contractor help desk
            </a>{" "}
            for any contractor-specific questions
          </li>
        </WSText.ParagraphSm>
      </WSList>
    </WSModal>
  );
};

export const useModalUndergoingUpgrades = () =>
  useWSModalCustom(ModalUndergoingUpgrades);
